import { Box, Container, Stack, Typography } from '@mui/material';

import type { ReactNode } from 'react';

/** Taille de l'icône décorative, en px. Un cran au-dessus du titre. */
const ICON_FONT_SIZE = 64;

export type AccessGateScreenProps = {
    /**
     * Pictogramme décoratif au-dessus du titre, en emoji.
     *
     * `aria-hidden` : il redit ce que le titre annonce déjà. Un seul des douze
     * écrans remplacés en portait un ; il est resté optionnel plutôt que
     * généralisé, faute de décision de design sur les onze autres.
     */
    icon?: string;
    /** Ce dont il s'agit. Rendu en `h1` — c'est le titre de la page. */
    title: string;
    /**
     * La promesse, quand l'écran cherche à convaincre plutôt qu'à informer :
     * les teasers d'inscription en ont une, les écrans d'accès refusé non.
     */
    lead?: string;
    /** L'explication, ou la raison du refus. */
    body?: string;
    /**
     * Les boutons. Aucun sur un accès refusé — le catalogue n'a pas de surface
     * équivalente à proposer au DMC — un ou deux sur un teaser.
     */
    actions?: ReactNode;
    'data-testid'?: string;
};

/**
 * Écran pleine page qui remplace un contenu que le rôle courant n'a pas le
 * droit de voir, ou qui n'existe pas.
 *
 * Douze écrans du Catalog en étaient des copies : trois teasers d'inscription
 * Visiteur, cinq accès refusés DMC, et quatre « introuvable » — dont trois
 * identiques au caractère près. Leurs différences étaient accidentelles : `h3`
 * contre `h4` pour le même niveau, l'espacement tantôt en `Stack spacing`
 * tantôt en `mb` sur chaque bloc, et un `Container` en `py: 8` au lieu de 10.
 *
 * Il n'y a **pas** de prop `kind` — le ticket en proposait une, mais après
 * unification les trois familles ne diffèrent plus que par le nombre de
 * boutons, ce que `actions` dit déjà.
 */
export function AccessGateScreen({
    icon,
    title,
    lead,
    body,
    actions,
    'data-testid': dataTestId,
}: AccessGateScreenProps) {
    return (
        <Container maxWidth="sm" sx={{ py: 10 }}>
            <Stack
                spacing={4}
                data-testid={dataTestId}
                sx={{ alignItems: 'center', textAlign: 'center' }}
            >
                {icon !== undefined && (
                    <Typography
                        variant="h2"
                        component="div"
                        sx={{ fontSize: ICON_FONT_SIZE }}
                        aria-hidden
                    >
                        {icon}
                    </Typography>
                )}
                <Box>
                    <Typography variant="h3" component="h1">
                        {title}
                    </Typography>
                    {lead !== undefined && (
                        <Typography variant="h4" component="h2" sx={{ mt: 2 }}>
                            {lead}
                        </Typography>
                    )}
                    {body !== undefined && (
                        <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
                            {body}
                        </Typography>
                    )}
                </Box>
                {actions !== undefined && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        {actions}
                    </Stack>
                )}
            </Stack>
        </Container>
    );
}
